import type {
	CreateProjectReviewInput,
	ProjectReviewEligibility,
	TargetReviewsResponse,
} from "@shared/types";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export interface ReviewEligibilityResult {
	success: boolean;
	data: ProjectReviewEligibility | null;
	error?: string;
}

export async function getProjectReviewEligibility(
	projectId: string,
): Promise<ReviewEligibilityResult> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/reviews`,
		);
		return {
			success: true,
			data: response.data.data,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			data: null,
			error: err.response?.data?.message || "Failed to load review eligibility",
		};
	}
}

export async function saveProjectReview(
	projectId: string,
	payload: CreateProjectReviewInput,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(`${API_BASE}/projects/${projectId}/reviews`, payload);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to save review",
		};
	}
}

/**
 * A designer's reviews and rating summary.
 *
 * Throws on failure rather than answering "no reviews", so a caller can tell a
 * broken request from a designer nobody has reviewed yet.
 */
export async function fetchDesignerReviews(
	designerId: string,
	limit = 5,
): Promise<TargetReviewsResponse> {
	const response = await apiClient.get(
		`${API_BASE}/designers/${designerId}/reviews`,
		{
			params: { limit },
		},
	);
	return response.data.data;
}

/**
 * A contractor's reviews and rating summary. Throws on failure, like
 * `fetchDesignerReviews`, so a page can say the reviews did not load rather
 * than show a contractor nobody has reviewed.
 */
export async function fetchContractorReviews(
	contractorId: string,
	limit = 5,
): Promise<TargetReviewsResponse> {
	const response = await apiClient.get(
		`${API_BASE}/contractors/${contractorId}/reviews`,
		{
			params: { limit },
		},
	);
	return response.data.data;
}

export async function getRealtorReviews(
	realtorId: string,
	limit = 5,
): Promise<TargetReviewsResponse> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/realtors/${realtorId}/reviews`,
			{
				params: { limit },
			},
		);
		return response.data.data;
	} catch {
		// Match the designer/contractor degradation: an error on the
		// public portfolio fetch should NOT break the page — we just
		// surface "no reviews yet" instead.
		return {
			summary: {
				averageRating: null,
				reviewCount: 0,
			},
			reviews: [],
		};
	}
}
